"use client";

import { Button } from "../ui/button";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import { Handlebars } from "./handlebars";
import { Link } from "@/i18n/navigation";
import { analyticsTrack } from "@/analytics/analytics";

const HERO_BENEFIT_STYLES = [
	{
		className:
			"border-blue-200 bg-blue-50/90 text-blue-900 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-200",
	},
	{
		className:
			"border-cyan-200 bg-cyan-50/90 text-cyan-900 dark:border-cyan-800 dark:bg-cyan-950/50 dark:text-cyan-200",
	},
	{
		className:
			"border-orange-200 bg-orange-50/90 text-orange-900 dark:border-orange-800 dark:bg-orange-950/50 dark:text-orange-200",
	},
	{
		className:
			"border-amber-200 bg-amber-50/90 text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200",
	},
] as const;

const HERO_SHOWCASE_CARDS = [
	"/landing/hero-cards/card-1.webp",
	"/landing/hero-cards/card-2.webp",
	"/landing/hero-cards/card-3.webp",
	"/landing/hero-cards/card-4.webp",
	"/landing/hero-cards/card-5.webp",
] as const;

function HeroShowcase({ label }: { label: string }) {
	return (
		<div
			className="hero-showcase mx-auto mt-5 w-full max-w-4xl"
			role="img"
			aria-label={label}
		>
			<div className="hero-showcase-glow" aria-hidden="true" />
			{HERO_SHOWCASE_CARDS.map((src, index) => (
				<div
					key={src}
					className={`hero-showcase-card hero-showcase-card-${index + 1}`}
					aria-hidden="true"
				>
					<Image
						src={src}
						alt=""
						width={320}
						height={426}
						unoptimized
						loading="eager"
						fetchPriority="low"
						className="size-full object-cover"
					/>
				</div>
			))}
		</div>
	);
}

export interface HeroContent {
	titlePrefix: string;
	titleHighlight: string;
	galleryLabel: string;
	benefitsLabel: string;
	benefits: readonly string[];
	cta: string;
}

export function Hero({ content }: { content: HeroContent }) {
	return (
		<div className="relative flex min-h-[calc(100svh-5rem)] flex-col items-center justify-between overflow-x-clip px-4 text-center">
			<div className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center py-8 sm:py-10">
				<h1 className="text-balance text-4xl leading-[1.2] font-bold tracking-tighter sm:text-5xl lg:text-[4rem]">
					{content.titlePrefix}{" "}
					<Handlebars>{content.titleHighlight}</Handlebars>
				</h1>

				<ul
					className="mx-auto mt-5 flex max-w-2xl flex-wrap items-center justify-center gap-2.5 sm:mt-6 sm:gap-3"
					aria-label={content.benefitsLabel}
				>
					{content.benefits.map((benefit, index) => (
						<li
							key={benefit}
							className={`rounded-full border px-4 py-2 text-sm font-semibold shadow-xs backdrop-blur-sm ${HERO_BENEFIT_STYLES[index]?.className ?? HERO_BENEFIT_STYLES[0].className}`}
						>
							{benefit}
						</li>
					))}
				</ul>

				<HeroShowcase label={content.galleryLabel} />

				<div className="mt-7 flex justify-center gap-8">
					<Button asChild size="lg" className="h-11 text-base">
						<Link
							href="/projects"
							onClick={() =>
								analyticsTrack({
									eventName: "editor_entry_clicked",
									properties: {
										surface: "landing_hero",
										destination: "/projects",
									},
								})
							}
						>
							{content.cta}
							<ArrowRight className="ml-0.5" />
						</Link>
					</Button>
				</div>
			</div>
		</div>
	);
}
